import type * as React from 'react'
import { useEffect, useRef, useState } from 'react'
import type { JSX } from 'react'

import { useMutation } from '@apollo/client/react'

import { Button, Flex, Form, Input, toast } from '@buffer-mono/popcorn'

import { useAccount } from '~publish/contexts/AccountContext'
import { graphql } from '~publish/graphql'

import style from '../ProfileSettings.module.css'

export const UPDATE_ACCOUNT_NAME = graphql(/* GraphQL */ `
  mutation UpdateAccountName($input: UpdateAccountNameInput!) {
    updateAccountName(input: $input) {
      __typename
      ... on UpdateAccountNameSuccess {
        _empty
      }
      ... on UnexpectedError {
        message
      }
      ... on InvalidInputError {
        message
      }
    }
  }
`)

const Name = (): JSX.Element => {
  const [updateAccountName] = useMutation(UPDATE_ACCOUNT_NAME)
  const { account, refetchAccount } = useAccount()
  const currentAccountName = account?.name
  const [updatedAccountName, setUpdatedAccountName] =
    useState(currentAccountName)
  const [dirty, setDirty] = useState(false)
  const [invalidMessage, setInvalidMessage] = useState<string | null>(null)
  const nameInputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (
      updatedAccountName !== undefined &&
      updatedAccountName !== currentAccountName
    ) {
      setDirty(true)
    } else {
      setDirty(false)
    }
  }, [updatedAccountName, currentAccountName])

  const handleSubmit = async (
    event: React.FormEvent<HTMLFormElement>,
  ): Promise<void> => {
    event.preventDefault()
    if (!updatedAccountName?.trim()) {
      setInvalidMessage('Enter your name.')
      nameInputRef.current?.focus()
      return
    }

    const result = await updateAccountName({
      variables: {
        input: {
          name: updatedAccountName ?? '',
        },
      },
    })
    if (
      result.data?.updateAccountName?.__typename !== 'UpdateAccountNameSuccess'
    ) {
      toast.error('An error occurred while updating your name.')
      setUpdatedAccountName(currentAccountName)
    } else {
      if (refetchAccount) {
        refetchAccount()
      }
      toast.success('Your name has been updated!')
    }
  }

  return (
    <Form
      name="updateAccountNameForm"
      className={style.fullWidth}
      onSubmit={handleSubmit}
      noValidate
    >
      <Form.Field
        name="name"
        controlId="name"
        className={style.fullWidth}
        invalid={!!invalidMessage}
        required
      >
        <Form.Label>Name</Form.Label>
        <Flex gap="xs" align="end" className={style.fullWidth}>
          <Form.Control>
            <Input
              ref={nameInputRef}
              className={style.flexInput}
              defaultValue={currentAccountName ?? ''}
              autoComplete="name"
              required
              onChange={(event): void => {
                setUpdatedAccountName(event.target.value)
                if (event.target.value.trim()) {
                  setInvalidMessage(null)
                }
              }}
            />
          </Form.Control>
          <Button
            size="medium"
            type="submit"
            variant="secondary"
            disabled={!dirty}
            className={style.fixedWidthButton}
          >
            Save Changes
          </Button>
        </Flex>
        <Form.Error>{invalidMessage}</Form.Error>
      </Form.Field>
    </Form>
  )
}

export default Name
